import {
  GET_SYNCFY_SESSION_TOKEN,
  GET_SYNCFY_SESSION_TOKEN_LOADING,
  GET_SYNCFY_ACCOUNTS,
  GET_SYNCFY_ACCOUNTS_LOADING,
  GET_SYNCFY_SITE_ORGANIZATIONS,
  GET_PAYMENT_RECONCILIATION,
  GET_PAYMENT_RECONCILIATION_LOADING,
  GET_PAYMENT_RECONCILIATION_INVOICES,
  GET_PAYMENT_RECONCILIATION_INVOICES_LOADING,
  SELECT_TRANSACTION_FOR_RECONCILIATION,
  SELECT_INVOICE_FOR_RECONCILIATION,
  DESELECT_INVOICE_FOR_RECONCILIATION,
  UPDATE_INVOICE_AMOUNT_TO_APPLY,
  CLEAR_RECONCILIATION_SELECTION,
  RECONCILE_PAYMENT,
  RECONCILE_PAYMENT_LOADING,
  BANK_IMPORT_PREVIEW_REQUEST,
  BANK_IMPORT_PREVIEW_SUCCESS,
  BANK_IMPORT_PREVIEW_ERROR,
  BANK_IMPORT_STATEMENT_REQUEST,
  BANK_IMPORT_STATEMENT_SUCCESS,
  BANK_IMPORT_CLEAR_PREVIEW,
  BANK_DELETE_TRANSACTION_REQUEST,
  BANK_DELETE_TRANSACTION_SUCCESS,
  BANK_DELETE_BATCH_REQUEST,
  BANK_DELETE_BATCH_SUCCESS
} from './actions';

const initialState = {
  syncfySessionToken: null,
  accounts: [],
  siteOrganizations: [],
  transactions: [],
  invoices: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  invoicesPagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  reconciliation: {
    selectedTransaction: null,
    selectedInvoices: []
  },
  loaders: {
    sessionToken: false,
    accounts: true,
    transactions: false,
    invoices: false,
    reconciling: false,
    importPreview: false,
    importStatement: false,
    deleteTransaction: false,
    deleteBatch: false
  },
  importPreview: {
    bankName: null,
    accountNumber: null,
    parsedBy: null,
    transactions: [],
    summary: null
  }
};

const bankReconciliationReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_SYNCFY_SESSION_TOKEN_LOADING:
      return {
        ...state,
        loaders: { ...state.loaders, sessionToken: true }
      };
    case GET_SYNCFY_SESSION_TOKEN:
      return {
        ...state,
        syncfySessionToken: action.payload,
        loaders: { ...state.loaders, sessionToken: false }
      };
    case GET_SYNCFY_ACCOUNTS_LOADING:
      return {
        ...state,
        loaders: { ...state.loaders, accounts: true }
      };
    case GET_SYNCFY_ACCOUNTS:
      return {
        ...state,
        accounts: action.payload,
        loaders: { ...state.loaders, accounts: false }
      };
    case GET_SYNCFY_SITE_ORGANIZATIONS:
      return {
        ...state,
        siteOrganizations: action.payload
      };
    case GET_PAYMENT_RECONCILIATION_LOADING:
      const shouldClearTransactions = action.payload && action.payload.clearTransactions;
      return {
        ...state,
        transactions: shouldClearTransactions ? [] : state.transactions,
        loaders: { ...state.loaders, transactions: true }
      };
    case GET_PAYMENT_RECONCILIATION:
      const newTransactions = action.payload.transactions || [];
      const newPagination = action.payload.pagination || state.pagination;
      const isLoadingMore = newPagination.page > 1;

      let finalTransactions;
      if (isLoadingMore) {
        const combined = [...state.transactions, ...newTransactions];
        finalTransactions = combined.filter((transaction, index, self) => 
          index === self.findIndex(t => t.uuid === transaction.uuid)
        );
      } else {
        finalTransactions = newTransactions;
      }

      return {
        ...state,
        transactions: finalTransactions,
        pagination: newPagination,
        loaders: { ...state.loaders, transactions: false }
      };
    case GET_PAYMENT_RECONCILIATION_INVOICES_LOADING:
      const shouldClearInvoices = action.payload && action.payload.clearInvoices;
      return {
        ...state,
        invoices: shouldClearInvoices ? [] : state.invoices,
        loaders: { ...state.loaders, invoices: true }
      };
    case GET_PAYMENT_RECONCILIATION_INVOICES:
      const allInvoices = action.payload.invoices || [];
      const newInvoices = allInvoices.filter(invoice => {
        const currency = invoice.currency || '';
        return currency.toUpperCase() === 'MXN';
      });
      const newInvoicesPagination = action.payload.pagination || state.invoicesPagination;
      const isLoadingMoreInvoices = newInvoicesPagination.page > 1;

      let finalInvoices;
      if (isLoadingMoreInvoices) {
        const combined = [...state.invoices, ...newInvoices];
        finalInvoices = combined.filter((invoice, index, self) => 
          index === self.findIndex(i => i.uuid === invoice.uuid)
        );
      } else {
        finalInvoices = newInvoices;
      }

      return {
        ...state,
        invoices: finalInvoices,
        invoicesPagination: newInvoicesPagination,
        loaders: { ...state.loaders, invoices: false }
      };
    case SELECT_TRANSACTION_FOR_RECONCILIATION:
      return {
        ...state,
        reconciliation: {
          selectedTransaction: action.payload,
          selectedInvoices: []
        }
      };
    case SELECT_INVOICE_FOR_RECONCILIATION:
      const isAlreadySelected = state.reconciliation.selectedInvoices.some(inv => inv.uuid === action.payload.uuid);

      if (isAlreadySelected) {
        return state;
      }

      const interestMoratorium = action.payload.interest_moratorium;
      const interestCalcs = interestMoratorium && interestMoratorium.calcs;
      const interestAmount = Number((interestCalcs && interestCalcs.amount_debt) || 0);
      const hasInterest = interestAmount > 0;
      const invoiceBalance = Number(action.payload.balance || 0);
      const transactionAmount = state.reconciliation.selectedTransaction
        ? Number(state.reconciliation.selectedTransaction.amount || 0)
        : 0;

      const totalAlreadyApplied = state.reconciliation.selectedInvoices.reduce((sum, inv) => {
        return sum + Number(inv.amountToApply || 0);
      }, 0);

      const remainingTransactionAmount = transactionAmount - totalAlreadyApplied;

      let amountToApply;
      let isAmountLocked = false;

      if (hasInterest) {
        amountToApply = interestAmount;
        isAmountLocked = true;
      } else if (transactionAmount > 0) {
        amountToApply = Math.min(invoiceBalance, remainingTransactionAmount);
      } else {
        amountToApply = invoiceBalance;
      }

      const invoiceWithAmount = {
        ...action.payload,
        amountToApply: amountToApply,
        isAmountLocked: isAmountLocked
      };

      return {
        ...state,
        reconciliation: {
          ...state.reconciliation,
          selectedInvoices: [...state.reconciliation.selectedInvoices, invoiceWithAmount]
        }
      };
    case DESELECT_INVOICE_FOR_RECONCILIATION:
      return {
        ...state,
        reconciliation: {
          ...state.reconciliation,
          selectedInvoices: state.reconciliation.selectedInvoices.filter(inv => inv.uuid !== action.payload)
        }
      };
    case UPDATE_INVOICE_AMOUNT_TO_APPLY:
      return {
        ...state,
        reconciliation: {
          ...state.reconciliation,
          selectedInvoices: state.reconciliation.selectedInvoices.map(inv =>
            inv.uuid === action.payload.invoiceUuid ? { ...inv, amountToApply: action.payload.amount } : inv
          )
        }
      };
    case CLEAR_RECONCILIATION_SELECTION:
      return {
        ...state,
        reconciliation: {
          selectedTransaction: null,
          selectedInvoices: []
        }
      };
    case RECONCILE_PAYMENT_LOADING:
      return {
        ...state,
        loaders: { ...state.loaders, reconciling: true }
      };
    case RECONCILE_PAYMENT:
      return {
        ...state,
        loaders: { ...state.loaders, reconciling: false }
      };

    case BANK_IMPORT_PREVIEW_REQUEST:
      return {
        ...state,
        loaders: { ...state.loaders, importPreview: true }
      };
    case BANK_IMPORT_PREVIEW_SUCCESS:
      return {
        ...state,
        importPreview: {
          bankName: action.payload.bank_name || null,
          accountNumber: action.payload.account_number || null,
          parsedBy: action.payload.parsed_by || null,
          transactions: action.payload.transactions || [],
          summary: action.payload.summary || null
        },
        loaders: { ...state.loaders, importPreview: false }
      };
    case BANK_IMPORT_PREVIEW_ERROR:
      return {
        ...state,
        loaders: { ...state.loaders, importPreview: false }
      };
    case BANK_IMPORT_STATEMENT_REQUEST:
      return {
        ...state,
        loaders: { ...state.loaders, importStatement: true }
      };
    case BANK_IMPORT_STATEMENT_SUCCESS:
      return {
        ...state,
        loaders: { ...state.loaders, importStatement: false }
      };
    case BANK_IMPORT_CLEAR_PREVIEW:
      return {
        ...state,
        importPreview: {
          bankName: null,
          accountNumber: null,
          parsedBy: null,
          transactions: [],
          summary: null
        }
      };
    case BANK_DELETE_TRANSACTION_REQUEST:
      return {
        ...state,
        loaders: { ...state.loaders, deleteTransaction: true }
      };
    case BANK_DELETE_TRANSACTION_SUCCESS:
      return {
        ...state,
        transactions: state.transactions.filter(t => t.uuid !== action.payload.uuid),
        loaders: { ...state.loaders, deleteTransaction: false }
      };
    case BANK_DELETE_BATCH_REQUEST:
      return {
        ...state,
        loaders: { ...state.loaders, deleteBatch: true }
      };
    case BANK_DELETE_BATCH_SUCCESS:
      return {
        ...state,
        loaders: { ...state.loaders, deleteBatch: false }
      };

    default:
      return state;
  }
};

export default bankReconciliationReducer;



// WEBPACK FOOTER //
// ./src/containers/bank-reconciliation/store/reducer.js